/**
 * Parser per file XML in formato FatturaPA (Fattura Elettronica).
 * Supporta sia il formato con namespace che senza.
 * Supporta anche file .p7m (PKCS#7): estrae l'XML dal contenuto firmato.
 * @see https://www.fatturapa.gov.it/
 */

/**
 * Estrae l'XML da un file .p7m (PKCS#7 signed-data).
 * Il contenuto firmato è spesso l'XML in chiaro; cerchiamo l'inizio e la fine del documento.
 * @param {ArrayBuffer} arrayBuffer - Contenuto del file .p7m
 * @returns {{ success: true, xml: string } | { success: false, error: string }}
 */
export function extractXmlFromP7m(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    return { success: false, error: 'File .p7m vuoto o non valido' };
  }
  const bytes = new Uint8Array(arrayBuffer);
  const markers = ['<?xml', '<FatturaElettronica', '<p:FatturaElettronica', '<ns0:FatturaElettronica', '<ns1:FatturaElettronica', '<ns2:FatturaElettronica', '<ns3:FatturaElettronica'];
  const xmlStartBytes = [0x3C, 0x3F, 0x78, 0x6D, 0x6C];
  let startIndex = -1;
  let str = '';

  const decoderUtf8 = new TextDecoder('utf-8', { fatal: false });
  const decoderLatin1 = new TextDecoder('iso-8859-1');
  str = decoderUtf8.decode(bytes);
  for (const m of markers) {
    const idx = str.indexOf(m);
    if (idx !== -1) {
      startIndex = idx;
      break;
    }
  }
  if (startIndex === -1) {
    str = decoderLatin1.decode(bytes);
    for (const m of markers) {
      const idx = str.indexOf(m);
      if (idx !== -1) {
        startIndex = idx;
        break;
      }
    }
  }
  if (startIndex === -1) {
    for (let i = 0; i <= bytes.length - xmlStartBytes.length; i++) {
      let match = true;
      for (let j = 0; j < xmlStartBytes.length; j++) {
        if (bytes[i + j] !== xmlStartBytes[j]) {
          match = false;
          break;
        }
      }
      if (match) {
        str = decoderUtf8.decode(bytes.slice(i));
        startIndex = 0;
        break;
      }
    }
  }
  if (startIndex === -1 && str) {
    for (const m of markers) {
      if (m === '<?xml') continue;
      const idx = str.indexOf(m);
      if (idx !== -1) {
        startIndex = idx;
        break;
      }
    }
  }
  if (startIndex === -1) {
    return { success: false, error: 'Nel file .p7m non è stato trovato alcun XML Fattura Elettronica. Verifica che il file sia un .p7m contenente una fattura elettronica (es. scaricato dal portale).' };
  }
  const fromStart = str.slice(startIndex);
  const closeTag = '</FatturaElettronica>';
  let endIndex = fromStart.lastIndexOf(closeTag);
  if (endIndex === -1) {
    const re = /<\/[\w]+:?FatturaElettronica\s*>/i;
    const match = fromStart.match(re);
    if (match) {
      endIndex = fromStart.lastIndexOf(match[0]) + match[0].length;
    }
  } else {
    endIndex += closeTag.length;
  }
  const xml = endIndex !== -1 ? fromStart.slice(0, endIndex) : fromStart;
  const trimmed = xml.trim();
  if (!trimmed || trimmed.length < 50) {
    return { success: false, error: 'Estratto dal .p7m troppo corto: possibile file corrotto o formato non supportato.' };
  }
  return { success: true, xml: trimmed };
}

function getChildByLocalName(parent, localName) {
  if (!parent || !parent.children) return null;
  const name = (localName || '').toLowerCase();
  for (let i = 0; i < parent.children.length; i++) {
    const el = parent.children[i];
    const elName = (el.localName || el.nodeName || '').toLowerCase();
    if (elName === name || el.nodeName === localName) return el;
  }
  return null;
}

function getTextContent(el) {
  if (!el) return '';
  const text = (el.textContent || el.nodeValue || '').trim();
  return text;
}

function getText(parent, localName) {
  const el = getChildByLocalName(parent, localName);
  return el ? getTextContent(el) : '';
}

function getFirstBody(doc) {
  const root = doc.documentElement || doc.getRootElement?.();
  if (!root) return null;
  const localName = (root.localName || root.nodeName || '').toLowerCase();
  if (localName !== 'fatturaelettronica') return null;
  const header = getChildByLocalName(root, 'FatturaElettronicaHeader');
  const body = getChildByLocalName(root, 'FatturaElettronicaBody');
  return { root, header, body };
}

const TIPO_DOCUMENTO_MAP = {
  TD01: 'Fattura',
  TD02: 'DDT',
  TD03: 'Fattura',
  TD04: 'Nota di credito',
  TD05: 'Nota di credito',
  TD06: 'Altro',
  TD16: 'Altro',
  TD17: 'Altro',
  TD18: 'Altro',
  TD19: 'Altro',
  TD20: 'Altro'
};

/**
 * Estrae i dati del CedentePrestatore (fornitore/emittente).
 * In FatturaPA Denominazione/Cognome/Nome sono dentro Anagrafica.
 */
function parseCedentePrestatore(header) {
  const cedente = getChildByLocalName(header, 'CedentePrestatore');
  if (!cedente) return null;
  const datiAnag = getChildByLocalName(cedente, 'DatiAnagrafici');
  const sede = getChildByLocalName(cedente, 'Sede');
  const anagrafica = datiAnag ? getChildByLocalName(datiAnag, 'Anagrafica') : null;
  let denominazione = '';
  if (anagrafica) {
    denominazione = getText(anagrafica, 'Denominazione');
    if (!denominazione) {
      const cognome = getText(anagrafica, 'Cognome');
      const nome = getText(anagrafica, 'Nome');
      denominazione = [cognome, nome].filter(Boolean).join(' ');
    }
  }
  if (!denominazione && datiAnag) {
    denominazione = getText(datiAnag, 'Denominazione') || '';
    if (!denominazione) {
      const cognome = getText(datiAnag, 'Cognome');
      const nome = getText(datiAnag, 'Nome');
      denominazione = [cognome, nome].filter(Boolean).join(' ');
    }
  }
  const idFiscale = datiAnag ? getChildByLocalName(datiAnag, 'IdFiscaleIVA') : null;
  const idPaese = idFiscale ? getText(idFiscale, 'IdPaese') : '';
  const idCodice = idFiscale ? getText(idFiscale, 'IdCodice') : '';
  const vatNumber = (idPaese && idCodice) ? `${idPaese}${idCodice}` : '';
  const fiscalCode = datiAnag ? getText(datiAnag, 'CodiceFiscale') || '' : '';
  const indirizzo = sede ? getText(sede, 'Indirizzo') : '';
  const numeroCivico = sede ? getText(sede, 'NumeroCivico') : '';
  const cap = sede ? getText(sede, 'CAP') : '';
  const comune = sede ? getText(sede, 'Comune') : '';
  const provincia = sede ? getText(sede, 'Provincia') : '';
  const nazione = sede ? getText(sede, 'Nazione') : '';
  const addressParts = [indirizzo, numeroCivico].filter(Boolean);
  const fullAddress = addressParts.length ? [addressParts.join(' '), cap, comune, provincia, nazione].filter(Boolean).join(', ') : '';
  return {
    name: denominazione.trim() || undefined,
    vatNumber: vatNumber || undefined,
    fiscalCode: fiscalCode || undefined,
    address: fullAddress || undefined,
    city: comune || undefined,
    postalCode: cap || undefined,
    province: provincia || undefined,
    country: nazione || 'Italia'
  };
}

/**
 * Estrae DatiGeneraliDocumento dal primo Body.
 */
function parseDatiGenerali(body) {
  const datiGen = getChildByLocalName(body, 'DatiGenerali');
  if (!datiGen) return null;
  const doc = getChildByLocalName(datiGen, 'DatiGeneraliDocumento');
  if (!doc) return null;
  const tipoDoc = getText(doc, 'TipoDocumento');
  const numero = getText(doc, 'Numero');
  const dataStr = getText(doc, 'Data');
  let dateFormatted = '';
  if (dataStr && dataStr.length >= 10) {
    dateFormatted = dataStr.slice(0, 10);
  }
  const importoTotale = getText(doc, 'ImportoTotaleDocumento');
  const divisa = getText(doc, 'Divisa');
  return {
    documentType: TIPO_DOCUMENTO_MAP[tipoDoc] || 'Fattura',
    invoiceNumber: numero || '',
    invoiceDate: dateFormatted,
    totalAmount: importoTotale ? parseFloat(importoTotale.replace(',', '.')) : 0,
    currency: divisa || 'EUR'
  };
}

/**
 * Estrae le aliquote IVA da DatiRiepilogo (riepilogo per aliquota) da usare come fallback se la riga non ha AliquotaIVA.
 */
function parseDatiRiepilogoAliquote(body) {
  const datiBeni = getChildByLocalName(body, 'DatiBeniServizi');
  if (!datiBeni) return [];
  const aliquote = [];
  const nodes = datiBeni.children || [];
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const localName = (el.localName || el.nodeName || '').toLowerCase();
    if (localName !== 'datariepilogo') continue;
    const aliquotaIva = getText(el, 'AliquotaIVA');
    if (aliquotaIva) {
      const val = parseFloat(aliquotaIva.replace(',', '.'));
      if (!Number.isNaN(val) && !aliquote.includes(val)) aliquote.push(val);
    }
  }
  return aliquote;
}

/**
 * Estrae le righe da DatiBeniServizi/DettaglioLinee.
 * Per ogni articolo importa:
 * - l'aliquota IVA da AliquotaIVA (DettaglioLinee); se assente usa la prima da DatiRiepilogo o 22.
 * - lo sconto (%) da ScontoMaggiorazione/Percentuale, se presente (primo sconto di tipo SC).
 */
function parseDettaglioLinee(body, defaultAliquote = []) {
  const defaultIva = defaultAliquote.length > 0 ? defaultAliquote[0] : 22;
  const datiBeni = getChildByLocalName(body, 'DatiBeniServizi');
  if (!datiBeni) return [];
  const items = [];
  const nodes = datiBeni.children || [];
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const localName = (el.localName || el.nodeName || '').toLowerCase();
    if (localName !== 'dettagliolinee') continue;
    const descrizione = getText(el, 'Descrizione');
    const quantita = getText(el, 'Quantita');
    const qty = parseFloat((quantita || '1').replace(',', '.')) || 1;
    const unitaMisura = getText(el, 'UnitaMisura') || 'pz';
    const prezzoUnitario = getText(el, 'PrezzoUnitario');
    const prezzoTotale = getText(el, 'PrezzoTotale');
    const unitPrice = prezzoUnitario ? parseFloat(prezzoUnitario.replace(',', '.')) : 0;
    const total = prezzoTotale ? parseFloat(prezzoTotale.replace(',', '.')) : unitPrice * qty;
    // Sconto/Maggiorazione: cerco il primo ScontoMaggiorazione di tipo sconto (SC) con Percentuale
    let discountPercent = 0;
    try {
      const collectSconti = (nodes) => {
        if (!nodes) return;
        for (let j = 0; j < nodes.length; j++) {
          const sm = nodes[j];
          const tipo = getText(sm, 'Tipo') || '';
          const tipoUpper = tipo.toUpperCase();
          if (!tipoUpper || !tipoUpper.startsWith('SC')) continue;
          const percStr = getText(sm, 'Percentuale');
          if (!percStr) continue;
          const percVal = parseFloat(String(percStr).replace(',', '.'));
          if (!Number.isNaN(percVal) && percVal > 0) {
            discountPercent = percVal;
            return;
          }
        }
      };
      if (el.getElementsByTagName) {
        const smNodes = el.getElementsByTagName('ScontoMaggiorazione');
        collectSconti(smNodes);
      }
      if (discountPercent === 0 && el.getElementsByTagNameNS) {
        const smNsNodes = el.getElementsByTagNameNS('*', 'ScontoMaggiorazione');
        collectSconti(smNsNodes);
      }
    } catch (_) {
      // in caso di errori di parsing sconto, ignoro e lascio 0
    }
    let aliquotaIva = getText(el, 'AliquotaIVA');
    if (!aliquotaIva && el.getElementsByTagName) {
      const anyIva = el.getElementsByTagName('AliquotaIVA');
      if (anyIva && anyIva.length > 0) aliquotaIva = (anyIva[0].textContent || '').trim();
    }
    if (!aliquotaIva && el.getElementsByTagNameNS) {
      const anyIva = el.getElementsByTagNameNS('*', 'AliquotaIVA');
      if (anyIva && anyIva.length > 0) aliquotaIva = (anyIva[0].textContent || '').trim();
    }
    const ivaNum = aliquotaIva ? parseFloat(String(aliquotaIva).replace(',', '.')) : defaultIva;
    const ivaPercent = Number.isNaN(ivaNum) ? defaultIva : ivaNum;
    const codArt = getChildByLocalName(el, 'CodiceArticolo');
    const productCode = codArt ? getText(codArt, 'CodiceValore') : '';
    items.push({
      productId: null,
      productCode: productCode || '',
      description: descrizione || 'Riga',
      quantity: qty,
      unitPrice,
      discountPercent,
      total,
      ivaPercent
    });
  }
  return items;
}

/**
 * Estrae la data di scadenza pagamento da DatiPagamento/DettaglioPagamento/DataScadenzaPagamento.
 */
function parsePaymentDueDate(body) {
  const datiPag = getChildByLocalName(body, 'DatiPagamento');
  if (!datiPag) return '';
  const dett = getChildByLocalName(datiPag, 'DettaglioPagamento');
  if (!dett) return '';
  const dataScad = getText(dett, 'DataScadenzaPagamento');
  if (!dataScad) return '';
  if (dataScad.length >= 10) return dataScad.slice(0, 10);
  return dataScad;
}

/**
 * Parsa un XML FatturaPA e restituisce { supplier, document, items }.
 * Se il file non è una FatturaElettronica (es. solo FileMetadati), restituisce null e msg.
 * @param {string} xmlString - Contenuto XML come stringa
 * @returns {{ success: true, supplier: object, document: object, items: array } | { success: false, error: string }}
 */
export function parseFatturaPAXml(xmlString) {
  if (!xmlString || typeof xmlString !== 'string') {
    return { success: false, error: 'File XML non valido o vuoto' };
  }
  const trimmed = xmlString.trim();
  if (trimmed.startsWith('<?xml')) {
    if (trimmed.includes('FileMetadati') && !trimmed.includes('FatturaElettronica')) {
      return { success: false, error: 'Il file contiene solo metadati (FileMetadati), non i dati della fattura. Usa il file XML della fattura elettronica (FatturaElettronica).' };
    }
  }
  let doc;
  try {
    const parser = new DOMParser();
    doc = parser.parseFromString(xmlString, 'text/xml');
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      return { success: false, error: 'Errore del parser XML. Verifica che il file sia un XML valido.' };
    }
  } catch (e) {
    return { success: false, error: e.message || 'Impossibile leggere il file XML' };
  }

  const { root, header, body } = getFirstBody(doc);
  if (!root) {
    return { success: false, error: 'File non riconosciuto come Fattura Elettronica (elemento radice FatturaElettronica non trovato).' };
  }
  if (!body) {
    return { success: false, error: 'File Fattura Elettronica senza corpo (FatturaElettronicaBody).' };
  }

  let supplier = header ? parseCedentePrestatore(header) : null;
  if (supplier && !(supplier.name && supplier.name.trim())) {
    supplier = { ...supplier, name: supplier.vatNumber ? `P.IVA ${supplier.vatNumber}` : supplier.fiscalCode ? `CF ${supplier.fiscalCode}` : 'Fornitore da registrare' };
  }
  const document = parseDatiGenerali(body);
  const defaultAliquote = parseDatiRiepilogoAliquote(body);
  const items = parseDettaglioLinee(body, defaultAliquote);
  const dueDate = parsePaymentDueDate(body);

  const baseDocument = document || { documentType: 'Fattura', invoiceNumber: '', invoiceDate: '', totalAmount: 0, currency: 'EUR' };
  const fullDocument = {
    ...baseDocument,
    ...(dueDate ? { dueDate } : {})
  };

  return {
    success: true,
    supplier: supplier || { name: 'Fornitore da registrare', vatNumber: undefined, fiscalCode: undefined },
    document: fullDocument,
    items: items.length > 0 ? items : [{ productId: null, productCode: '', description: 'Riga da fattura', quantity: 1, unitPrice: 0, discountPercent: 0, ivaPercent: 22, total: 0 }]
  };
}

export default parseFatturaPAXml;
